<template>
  <div class="layer-widget-panel scrollbar">
    <div class="measurements-container">
      <div class="measurements">
        <div class="form-group hidden position">
          <div class="form-row">
            <div class="input-group col">
              <div class="input-label">
                <span class="input-group-text">X</span>
              </div>
              <input
                type="number"
                class="form-control"
                id="layer-x"
                v-model="xPosition"
                :disabled="isWidgetLocked"
              />
            </div>

            <div class="input-group col">
              <div class="input-label">
                <span class="input-group-text">Y</span>
              </div>
              <input
                type="number"
                class="form-control"
                v-model="yPosition"
                :disabled="isWidgetLocked"
              />
            </div>
          </div>
        </div>

        <div class="form-group hidden size">
          <div class="form-row">
            <div class="input-group col">
              <div class="input-label">
                <span class="input-group-text">W</span>
              </div>
              <input
                type="number"
                class="form-control"
                id="layer-width"
                v-model="width"
                :disabled="isWidgetLocked"
              />
            </div>

            <div class="input-group col">
              <div class="input-label">
                <span class="input-group-text">H</span>
              </div>
              <input
                type="number"
                class="form-control"
                v-model="height"
                :disabled="isWidgetLocked"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="extra-icons">
        <LockIcon v-show="isWidgetLocked" :locked="isWidgetLocked" />
        <VisibilityIcon :isVisible="isWidgetVisible" @clicked="updateVisibility" />
        <PixelIcon :usePixels="usePixels" @clicked="changeValuesMode" />
      </div>
    </div>

    <div class="form-group template-input">
      <label for="template-name">Name</label>
      <input
        class="form-control"
        type="text"
        id="template-name"
        placeholder="Name"
        v-model="value.object.name"
      />
    </div>

    <div class="content-section">
      <div class="form-group template-input">
        <label for="rss-link">RSS Link</label>
        <div class="rss-link-edit">
          <input
            class="form-control"
            type="text"
            id="rss-link"
            :disabled="!editingRssLink"
            placeholder="Enter RSS Link"
            :value="editingRssLink ? tempRssLink : value.object.link"
            :title="!editingRssLink ? value.object.link : ''"
            @input="rssLinkInput"
          />
          <div class="rss-link-actions">
            <template v-if="editingRssLink">
              <i
                class="material-icons-outlined align-middle button"
                title="Accept changes"
                @click="saveRssLink"
              >
                check
              </i>
              <i
                class="material-icons-outlined align-middle button"
                title="Cancel changes"
                @click="cancelRssLinkEdit"
              >
                close
              </i>
            </template>
            <i
              v-else
              class="material-icons-outlined align-middle button rss-link-edit-button"
              title="Change Link"
              @click="startRssLinkEdit"
            >
              edit
            </i>
          </div>
        </div>
        <div v-if="editingRssLink && rssLinkError" class="rss-link-error">
          {{ rssLinkError }}
        </div>
      </div>
    </div>

    <div class="content-section">
      <div class="form-row text-css">
        <div class="col form-group layout-input">
          <label for="rss-duration">
            <i class="material-icons-outlined align-middle">timer</i>
            Duration
          </label>

          <input
            type="number"
            class="form-control"
            id="rss-duration"
            :disabled="isWidgetLockedByOverride"
            v-model="rssSlideDuration"
          />
        </div>

        <div class="col form-group layout-input">
          <label for="content-size">
            <i class="material-icons align-middle">text_fields</i>
            Title
          </label>

          <input
            type="number"
            class="form-control"
            id="title-size"
            :disabled="isWidgetLockedByOverride"
            v-model="titleFontSize"
          />
        </div>
      </div>

      <div class="form-row text-css">
        <div class="col form-group layout-input">
          <label for="content-size">
            <img src="@/assets/icon/padding-icon.svg" alt="" />
            Spacing
          </label>
          <input
            type="number"
            class="form-control"
            id="rss-spacing"
            :disabled="isWidgetLockedByOverride"
            v-model="rssSpacing"
          />
        </div>

        <div class="col form-group layout-input">
          <label for="content-size">
            <i class="material-icons-outlined align-middle">pause_presentation</i>
            Padding
          </label>

          <input
            type="number"
            class="form-control"
            id="padding"
            :disabled="isWidgetLockedByOverride"
            v-model="rssPadding"
          />
        </div>
      </div>
    </div>

    <div class="content-section">
      <div class="test">
        <div class="section-title">
          <div class="form-group">
            <div class="custom-control custom-switch layouts">
              <input
                type="checkbox"
                class="custom-control-input"
                id="rss-name"
                :disabled="isWidgetLockedByOverride"
                v-model="value.object.display.rssName"
              />
              <label class="custom-control-label" for="rss-name">Feed name</label>
            </div>
          </div>
        </div>
      </div>

      <div v-if="value.object.display.rssName" class="form-row text-css">
        <div class="col form-group layout-input">
          <label for="feed-name-size">
            <i class="material-icons align-middle">text_fields</i>
            Font size
          </label>

          <input
            type="number"
            class="form-control"
            id="feed-name-size"
            :disabled="isWidgetLockedByOverride"
            v-model="feedNameSize"
          />
        </div>
      </div>

      <div class="content-section">
        <div class="section-title">
          <div class="custom-control custom-switch layouts">
            <input
              type="checkbox"
              class="custom-control-input"
              id="rss-content"
              :disabled="isWidgetLockedByOverride"
              v-model="value.object.display.content"
            />
            <label class="custom-control-label" for="rss-content">Content</label>
          </div>
        </div>

        <div v-if="value.object.display.content" class="form-row text-css">
          <div class="col form-group layout-input">
            <label for="content-size">
              <i class="material-icons align-middle">text_fields</i>
              Font size
            </label>

            <input
              type="number"
              class="form-control"
              id="content-size"
              :disabled="isWidgetLockedByOverride"
              v-model="contentSize"
            />
          </div>

          <div class="col form-group layout-input">
            <label for="content-size">
              <i class="material-icons-outlined align-middle">content_cut</i>
              Characters limit
            </label>

            <input
              type="number"
              class="form-control"
              id="content-size"
              :disabled="isWidgetLockedByOverride"
              v-model="contentLength"
            />
          </div>
        </div>
      </div>

      <div class="content-section">
        <div class="section-title">
          <div class="custom-control custom-switch layouts">
            <input
              type="checkbox"
              class="custom-control-input"
              id="rss-image"
              :disabled="isWidgetLockedByOverride"
              v-model="value.object.display.image"
            />
            <label class="custom-control-label" for="rss-image">Feed Image</label>
          </div>
        </div>

        <image-mode
          v-if="value.object.display.image"
          :disabled="isWidgetLockedByOverride"
          v-model="value.object.display.imageMode"
        />

        <div
          v-if="value.object.display.image"
          class="form-group layout-input text-size slider-container"
        >
          <label for="image-width" class="slider"> Image Width </label>
          <input
            type="range"
            class="custom-range"
            min="20"
            :disabled="isWidgetLockedByOverride"
            v-model="value.object.display.imageWidth"
            max="80"
            step="1"
            id="image-width"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { restrictToBounds } from '@/helpers/draggable';
  import PixelIcon from '@/components/layoutDesigner/editor/settingPanel/PixelIcon.vue';
  import LockIcon from '@/components/layoutDesigner/editor/settingPanel/LockIcon.vue';
  import VisibilityIcon from '@/components/layoutDesigner/editor/settingPanel/VisibilityIcon.vue';
  import ImageMode from '@/components/templates/editor/settingPanel/formControls/ImageMode.vue';
  import widgetMixin from '@/components/templates/editor/settingPanel/widgetMixin';
  import { apiGetRss } from '@/api/rss';

  export default {
    name: 'RssWidgetPanel',

    mixins: [widgetMixin],

    components: {
      ImageMode,
      PixelIcon,
      LockIcon,
      VisibilityIcon,
    },

    data() {
      return {
        editingRssLink: false,
        tempRssLink: '',
        rssLinkError: '',
      };
    },

    computed: {
      feedNameSize: {
        get() {
          return this.value.object.display?.feedNameSize || 20;
        },
        set(value) {
          this.$set(
            this.value.object.display,
            'feedNameSize',
            restrictToBounds(Number(value), 0, 200),
          );
        },
      },

      titleFontSize: {
        get() {
          return this.value.object.display?.titleFontSize || 30;
        },
        set(value) {
          this.$set(
            this.value.object.display,
            'titleFontSize',
            restrictToBounds(Number(value), 0, 200),
          );
        },
      },

      contentSize: {
        get() {
          return this.value.object.display?.contentSize || 20;
        },
        set(value) {
          this.$set(
            this.value.object.display,
            'contentSize',
            restrictToBounds(Number(value), 0, 200),
          );
        },
      },

      contentLength: {
        get() {
          return this.value.object.display?.contentLength || 200;
        },
        set(value) {
          this.$set(
            this.value.object.display,
            'contentLength',
            restrictToBounds(Number(value), 1, 100000),
          );
        },
      },

      rssSpacing: {
        get() {
          return this.value.object.display?.spacing || 16;
        },
        set(value) {
          this.$set(this.value.object.display, 'spacing', restrictToBounds(Number(value), 0, 200));
        },
      },

      rssPadding: {
        get() {
          return this.value.object.display?.padding || 16;
        },
        set(value) {
          this.$set(this.value.object.display, 'padding', restrictToBounds(Number(value), 0, 200));
        },
      },

      rssSlideDuration: {
        get() {
          return this.value.object.display?.duration || 16;
        },
        set(value) {
          this.$set(
            this.value.object.display,
            'duration',
            restrictToBounds(Number(value), 1, 3600),
          );
        },
      },
    },

    methods: {
      updateVisibility() {
        this.value.object.visible = !this.value.object.visible;
      },

      changeValuesMode() {
        this.$emit('changeValuesMode');
      },

      startRssLinkEdit() {
        this.editingRssLink = true;
        this.tempRssLink = this.value.object.link;
        this.rssLinkError = '';
      },

      cancelRssLinkEdit() {
        this.editingRssLink = false;
        this.tempRssLink = '';
        this.rssLinkError = '';
      },

      rssLinkInput(event) {
        this.tempRssLink = event.target.value;
      },

      async validateRssLink(url) {
        if (!url) {
          this.rssLinkError = 'Please enter a valid RSS URL';
          return false;
        }

        this.rssLinkError = '';

        try {
          const response = await apiGetRss(url);
          const data = response.data;

          const rssLinkInvalid = !data.title || data.items.length === 0;

          if (rssLinkInvalid) {
            this.rssLinkError = `'${url}' is not a valid RSS URL`;
            return false;
          } else {
            this.rssLinkError = '';
            return true;
          }
        } catch (error) {
          this.rssLinkError = 'Error validating RSS URL';
          return false;
        }
      },

      async saveRssLink() {
        const isValid = await this.validateRssLink(this.tempRssLink);

        if (isValid) {
          this.$set(this.value.object, 'link', this.tempRssLink);
          this.editingRssLink = false;
          this.tempRssLink = '';
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .layer-widget-panel {
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    margin-right: 0px;
    padding-right: 10px;

    .button-2-secondary {
      margin: 16px 0;
      width: 100%;
    }
  }

  .form-group {
    .row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      margin: 0;
    }
  }

  .content-section {
    padding-top: 6px;

    .section-title {
      font-weight: bold;
      padding-top: 16px;
      font-size: 16px;
      border-top: 1px solid $lineGrey;
      margin-bottom: 10px;
    }
  }

  label {
    margin-bottom: 4px;
  }

  .sub-section-title {
    margin-top: 8px;
  }

  .slider-container {
    margin-top: 1rem;
  }

  .slider {
    display: block;
    margin-bottom: 0.5rem;
  }

  .text-css {
    display: flex;
    flex-wrap: wrap;
  }

  .rss-link-display {
    display: flex;
    align-items: center;
  }

  .rss-link-edit {
    display: flex;
    align-items: center;
    gap: 10px;

    .form-control {
      flex: 1;
      font-size: 12px;
    }

    .rss-link-actions {
      display: flex;
      gap: 5px;

      .btn {
        padding: 4px 8px;

        i {
          font-size: 16px;
        }
      }
    }
  }

  .show-searh-button {
    width: 100%;
    text-align: right;
    margin-top: -8px;

    &:hover {
      cursor: pointer;
      color: $primaryRed;
    }
  }

  .rss-link-error {
    color: #dc3545;
    font-size: 12px;
    margin-top: 5px;
  }

  .rss-link-edit-button {
    color: $secondaryText;
  }
</style>
